// Utility for prefetching routes in the background
import { lazy } from 'react';

// Map of routes to their lazy-loaded components
const routeMap = {
  '/how-it-works': () => import('../pages/HowItWorksPage'),
  '/bright-plan': () => import('../pages/EarlyAccess'),
  '/demo': () => import('../pages/Demo'),
  '/timeline': () => import('../pages/Timeline'),
  '/dashboard': () => import('../pages/Dashboard'),
};

// Priority order for prefetching (most important routes first)
const prefetchPriority = [
  '/how-it-works',
  '/bright-plan',
  '/demo',
  '/timeline',
];

/**
 * Prefetch routes in the background after initial page load
 * This improves navigation performance by loading page components
 * during idle time
 */
export const prefetchRoutes = () => {
  // Only prefetch if the document is fully loaded
  if (document.readyState !== 'complete') {
    window.addEventListener('load', () => {
      setTimeout(startPrefetching, 1000); // Wait 1s after load
    });
  } else {
    setTimeout(startPrefetching, 1000);
  }
};

/**
 * Start the prefetching process with staggered timing
 * to avoid overwhelming the network
 */
const startPrefetching = () => {
  // Check for slow connections and reduce prefetching
  const connection = (navigator as any).connection;
  const isSlowConnection = connection && 
    (connection.saveData || 
     connection.effectiveType === 'slow-2g' || 
     connection.effectiveType === '2g');

  // Adjust the number of routes to prefetch based on connection
  const routesToPrefetch = isSlowConnection 
    ? prefetchPriority.slice(0, 2) // Only prefetch 2 most important routes on slow connections
    : prefetchPriority;

  // Stagger prefetching to avoid network congestion
  routesToPrefetch.forEach((route, index) => {
    const delay = index * 300; // 300ms between each prefetch
    setTimeout(() => {
      if (routeMap[route]) {
        console.log(`Prefetching: ${route}`);
        routeMap[route]();
      }
    }, delay);
  });
};

/**
 * Prefetch a specific route on user interaction (hover/focus)
 * @param path Route path to prefetch
 */
export const prefetchRoute = (path: string) => {
  if (routeMap[path]) {
    routeMap[path]();
  }
};

export default routeMap;
